import React from 'react'
import {
  DepartmentNavigationTopLevelItemFragment,
  DepartmentNavigationResponsiveSmallSizeOptions,
  DepartmentNavigationResponsiveMixinFragment
} from '#src/generated/graphql'
import CollapseIcon from './collapse.svg'
import ExpandIcon from './expand.svg'
import FlyoutMenu from '#src/components/FlyoutMenu'
import MobileNavigationSubSection from '../MobileNavigationSubSection'
import DepartmentNavigationLink from '../../../common/DepartmentNavigationLink'
import { Box } from '@deal/components'
import classnames from 'classnames'
import styles from './styles.css'

interface MobileTertiaryNavProps {
  items: DepartmentNavigationTopLevelItemFragment[]
  // Determines whether the menu is visible
  open: boolean
  // Close the menu
  onClose?: () => void
}

interface MobileTertiaryNavState {
  // Which items are expanded
  collapsed: {
    [itemId: string]: boolean
  }
}

/**
 * The browse department menu serves as the third-level nav for mobile users.
 *
 * The menu's content is related to the active department (because the secondary nav has limited space):
 *   - Browsing categories and sub-categories
 *   - Searching via recommended facets (at the department level)
 */
class V2MobileTertiaryNav extends React.PureComponent<
  MobileTertiaryNavProps,
  MobileTertiaryNavState
> {
  public constructor(props: MobileTertiaryNavProps) {
    super(props)

    this.state = {
      collapsed: {}
    }

    this.toggleItemCollapsed = this.toggleItemCollapsed.bind(this)
  }

  public toggleItemCollapsed(itemId: string) {
    this.setState(prevState => ({
      collapsed: {
        ...prevState.collapsed,
        [itemId]: !prevState.collapsed[itemId]
      }
    }))
  }

  public shouldShowItem(item: DepartmentNavigationResponsiveMixinFragment) {
    return (
      item.showIfSmallSize &&
      item.smallSizeOptions.find(
        option => option === DepartmentNavigationResponsiveSmallSizeOptions.SHOW_IN_FLYOUT_PANEL
      )
    )
  }

  public render() {
    const { items, open, onClose } = this.props

    return (
      <FlyoutMenu from="right" open={open} onClose={onClose} title="Browse">
        {items.filter(this.shouldShowItem).map(item => {
          const subSections = item.subSections.filter(this.shouldShowItem)
          const hasSubSections = subSections.length > 0
          const isCollapsed = this.state.collapsed[item.id]

          return (
            <Box.Item
              className={classnames(styles.item, {
                [styles.itemCollapsed]: isCollapsed,
                [styles.itemHasSubSections]: hasSubSections
              })}
              shrink={0}
              key={item.id}
            >
              <div className={styles.section}>
                <div
                  className={styles.sectionHeader}
                  onClick={() => hasSubSections && this.toggleItemCollapsed(item.id)}
                >
                  {item.labelLink && !hasSubSections ? (
                    <DepartmentNavigationLink
                      link={item.labelLink}
                      replace={hasSubSections}
                      className={styles.sectionHeaderLink}
                      onClick={this.props.onClose}
                    >
                      {item.label}
                    </DepartmentNavigationLink>
                  ) : (
                    <span
                      className={styles.sectionHeaderLink}
                      onClick={hasSubSections ? undefined : this.props.onClose}
                    >
                      {item.label}

                      {hasSubSections && (
                        <>
                          <CollapseIcon className={styles.collapseIcon} />
                          <ExpandIcon className={styles.expandIcon} />
                        </>
                      )}
                    </span>
                  )}
                </div>

                {subSections.map(section => {
                  return (
                    <div className={styles.subSectionContainer} key={section.id}>
                      <MobileNavigationSubSection section={section} onLinkClick={onClose} />
                    </div>
                  )
                })}
              </div>
            </Box.Item>
          )
        })}
      </FlyoutMenu>
    )
  }
}

export default V2MobileTertiaryNav
